import React, { useState, useEffect } from 'react';
import {
	Modal,
	ModalHeader,
	ModalBody,
	ModalFooter,
	Button,
	Input,
	FormGroup,
	Label,
	Table,
	Badge,
	Spinner,
	Alert,
} from 'reactstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { BASE_URL } from '../constants/apiConfig';
import './ShareDashboardModal.css';

// Dashboard Limit Exceeded Modal Component
const DashboardLimitModal = ({ isOpen, toggle, savedDashboards, onDelete }) => {
	const [selectedDashboard, setSelectedDashboard] = useState(null);

	const handleDelete = async () => {
		if (!selectedDashboard) {
			toast.warning('Please select a dashboard to delete');
			return;
		}

		if (!window.confirm(`Are you sure you want to delete "${selectedDashboard.label}"?`)) {
			return;
		}

		try {
			const token = sessionStorage.getItem('token');
			await axios.delete(`${BASE_URL}/api/admin/dashboard/saved/${selectedDashboard.id}`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			toast.success('Dashboard deleted successfully!');
			onDelete();
			setSelectedDashboard(null);
		} catch (error) {
			console.error('Error deleting dashboard:', error);
			toast.error('Failed to delete dashboard');
		}
	};

	return (
		<Modal isOpen={isOpen} toggle={toggle} size="md">
			<ModalHeader toggle={toggle}>
				<i className="bi bi-exclamation-triangle text-warning me-2"></i>
				Dashboard Limit Reached
			</ModalHeader>
			<ModalBody>
				<p>You have reached the maximum limit of 5 saved dashboards.</p>
				<p>To save a new dashboard, please delete one of your existing dashboards first:</p>

				<FormGroup>
					<Label for="selectDashboard">Select Dashboard to Delete</Label>
					<Input
						type="select"
						id="selectDashboard"
						value={selectedDashboard?.id || ''}
						onChange={(e) => {
							const dashboard = savedDashboards.find(d => d.id === parseInt(e.target.value));
							setSelectedDashboard(dashboard);
						}}>
						<option value="">Choose a dashboard...</option>
						{savedDashboards.map((dashboard) => (
							<option key={dashboard.id} value={dashboard.id}>
								{dashboard.label} (Saved: {new Date(dashboard.created_at).toLocaleDateString()})
							</option>
						))}
					</Input>
				</FormGroup>

				{selectedDashboard && (
					<Alert color="warning" className="mt-3">
						<strong>Selected:</strong> {selectedDashboard.label}
						<br />
						<small className="text-muted">
							Saved on {new Date(selectedDashboard.created_at).toLocaleString()}
						</small>
					</Alert>
				)}
			</ModalBody>
			<ModalFooter>
				<Button color="secondary" onClick={toggle}>
					Cancel
				</Button>
				<Button
					color="danger"
					onClick={handleDelete}
					disabled={!selectedDashboard}>
					<i className="bi bi-trash me-2"></i>
					Delete Selected Dashboard
				</Button>
			</ModalFooter>
		</Modal>
	);
};

const ShareDashboardModal = ({ isOpen, toggle, dashboardToShare = null, currentDashboardState }) => {
	const [users, setUsers] = useState([]);
	const [selectedUsers, setSelectedUsers] = useState([]);
	const [searchTerm, setSearchTerm] = useState('');
	const [loading, setLoading] = useState(false);
	const [sending, setSending] = useState(false);
	const [message, setMessage] = useState('');
	const [showLimitModal, setShowLimitModal] = useState(false);
	const [savedDashboards, setSavedDashboards] = useState([]);
	const [showSuccessModal, setShowSuccessModal] = useState(false);
	const [showErrorModal, setShowErrorModal] = useState(false);
	const [errorMessage, setErrorMessage] = useState('');
	const [successMessage, setSuccessMessage] = useState('');

	// Fetch users when modal opens
	useEffect(() => {
		if (isOpen) {
			fetchUsers();
			fetchSavedDashboards();
		}
	}, [isOpen]);

	const fetchUsers = async () => {
		setLoading(true);
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(`${BASE_URL}/api/admin/users`, {
				params: { excludeRole: 'applicant' },
				headers: { Authorization: `Bearer ${token}` },
			});

			if (response.data.success) {
				const users = response.data.users || [];
				setUsers(users);
			}
		} catch (error) {
			console.error('Error fetching users:', error);
			toast.error('Failed to load users');
		} finally {
			setLoading(false);
		}
	};

	const fetchSavedDashboards = async () => {
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.get(`${BASE_URL}/api/admin/dashboard/saved`, {
				headers: { Authorization: `Bearer ${token}` },
			});

			if (response.data.success) {
				setSavedDashboards(response.data.data);
			}
		} catch (error) {
			console.error('Error fetching saved dashboards:', error);
			// Don't show toast for this error as it's not critical
		}
	};

	const handleUserSelect = (userId) => {
		setSelectedUsers((prev) => {
			if (prev.includes(userId)) {
				return prev.filter((id) => id !== userId);
			} else {
				return [...prev, userId];
			}
		});
	};

	const handleSelectAll = () => {
		if (selectedUsers.length === filteredUsers.length) {
			setSelectedUsers([]);
		} else {
			setSelectedUsers(filteredUsers.map((user) => user.id));
		}
	};

	const handleSend = async () => {
		if (selectedUsers.length === 0) {
			toast.warning('Please select at least one user');
			return;
		}

		// If sharing current state and no dashboard is selected, save it first
		let dashboardToSend = dashboardToShare;
		if (!dashboardToShare && currentDashboardState) {
			try {
				const token = sessionStorage.getItem('token');
				const saveResponse = await axios.post(
					`${BASE_URL}/api/admin/dashboard/save`,
					{
						label: `Shared Dashboard - ${new Date().toLocaleString()}`,
						state: currentDashboardState,
					},
					{
						headers: { Authorization: `Bearer ${token}` },
					}
				);

				if (saveResponse.data.success) {
					dashboardToSend = saveResponse.data.data;
					toast.info('Dashboard saved before sharing');
				} else if (saveResponse.data.errorCode === 'MAX_SAVED_DASHBOARDS') {
					// Show limit modal instead of toast
					setShowLimitModal(true);
					return;
				} else {
					throw new Error('Failed to save dashboard');
				}
			} catch (saveError) {
				console.error('Error saving dashboard:', saveError);
				if (saveError.response?.data?.errorCode === 'MAX_SAVED_DASHBOARDS') {
					setShowLimitModal(true);
					return;
				}
				toast.error('Failed to save dashboard before sharing');
				return;
			}
		}

		setSending(true);
		try {
			const token = sessionStorage.getItem('token');
			const response = await axios.post(
				`${BASE_URL}/api/admin/dashboard/share`,
				{
					userIds: selectedUsers,
					dashboardId: dashboardToSend?.id || null,
					dashboardState: dashboardToSend?.state || currentDashboardState,
					message: message.trim(),
				},
				{
					headers: { Authorization: `Bearer ${token}` },
				}
			);

			if (response.data.success) {
				setSuccessMessage(`Dashboard shared successfully with ${selectedUsers.length} user(s)!`);
				setShowSuccessModal(true);
				setSelectedUsers([]);
				setMessage('');
				toggle();
			}
		} catch (error) {
			console.error('Error sharing dashboard:', error);
			setErrorMessage(error.response?.data?.message || 'Failed to share dashboard');
			setShowErrorModal(true);
		} finally {
			setSending(false);
		}
	};

	// Filter users based on search term
	const filteredUsers = users.filter(
		(user) =>
			user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
			user.email?.toLowerCase().includes(searchTerm.toLowerCase())
	);

	const handleLimitModalClose = () => {
		setShowLimitModal(false);
	};

	const handleLimitModalDelete = () => {
		fetchSavedDashboards();
		setShowLimitModal(false);
	};

	return (
		<>
			<Modal isOpen={isOpen} toggle={toggle} size="lg" className="share-dashboard-modal">
				<ModalHeader toggle={toggle}>
					Share Dashboard
					{dashboardToShare && (
						<Badge color="primary" className="ms-2">
							{dashboardToShare.label}
						</Badge>
					)}
				</ModalHeader>
				<ModalBody>
					{dashboardToShare && (
						<Alert color="info" className="mb-3">
							<i className="bi bi-info-circle me-2"></i>
							Sharing saved dashboard: <strong>{dashboardToShare.label}</strong>
						</Alert>
					)}

					{/* Optional Message */}
					<FormGroup className="mb-3">
						<Label for="shareMessage">Message (Optional)</Label>
						<Input
							type="textarea"
							id="shareMessage"
							placeholder="Add a message for the recipients..."
							value={message}
							onChange={(e) => setMessage(e.target.value)}
							rows={3}
							maxLength={500}
						/>
						<small className="text-muted">{message.length}/500 characters</small>
					</FormGroup>

					{/* Search Users */}
					<FormGroup className="mb-3">
						<Label for="searchUsers">Search Users</Label>
						<Input
							type="text"
							id="searchUsers"
							placeholder="Search by name or email..."
							value={searchTerm}
							onChange={(e) => setSearchTerm(e.target.value)}
						/>
					</FormGroup>

					{/* Users List */}
					<div className="users-section">
						<div className="d-flex justify-content-between align-items-center mb-2">
							<h6 className="mb-0">
								Select Users ({selectedUsers.length} selected)
							</h6>
							{filteredUsers.length > 0 && (
								<Button
									color="link"
									size="sm"
									onClick={handleSelectAll}
									className="p-0">
									{selectedUsers.length === filteredUsers.length ? 'Deselect All' : 'Select All'}
								</Button>
							)}
						</div>

						{loading ? (
							<div className="text-center py-4">
								<Spinner color="primary" />
								<p className="mt-2">Loading users...</p>
							</div>
						) : filteredUsers.length === 0 ? (
							<Alert color="warning" className="text-center">
								<i className="bi bi-search me-2"></i>
								{searchTerm ? 'No users found matching your search' : 'No users available'}
							</Alert>
						) : (
							<div className="users-table-container">
								<Table hover className="users-table">
									<thead>
										<tr>
											<th style={{ width: '50px' }}>
												<Input
													type="checkbox"
													checked={selectedUsers.length === filteredUsers.length}
													onChange={handleSelectAll}
												/>
											</th>
											<th>Name</th>
											<th>Email</th>
											<th>Role</th>
										</tr>
									</thead>
									<tbody>
										{filteredUsers.map((user) => (
											<tr
												key={user.id}
												onClick={() => handleUserSelect(user.id)}
												className={selectedUsers.includes(user.id) ? 'selected' : ''}
												style={{ cursor: 'pointer' }}>
												<td onClick={(e) => e.stopPropagation()}>
													<Input
														type="checkbox"
														checked={selectedUsers.includes(user.id)}
														onChange={() => handleUserSelect(user.id)}
													/>
												</td>
												<td>
													<strong>{user.name}</strong>
												</td>
												<td className="text-muted">{user.email}</td>
												<td>
													<Badge color={user.role === 'reviewer' ? 'info' : 'secondary'}>
														{user.role || 'User'}
													</Badge>
												</td>
											</tr>
										))}
									</tbody>
								</Table>
							</div>
						)}
					</div>
				</ModalBody>
				<ModalFooter>
					<div className="d-flex justify-content-between align-items-center w-100">
						<div className="text-muted">
							{selectedUsers.length > 0 && (
								<small>
									<i className="bi bi-people-fill me-1"></i>
									{selectedUsers.length} user(s) selected
								</small>
							)}
						</div>
						<div>
							<Button color="secondary" onClick={toggle} className="me-2">
								Cancel
							</Button>
							<Button
								color="primary"
								onClick={handleSend}
								disabled={sending || selectedUsers.length === 0}>
								{sending ? (
									<>
										<Spinner size="sm" className="me-2" />
										Sending...
									</>
								) : (
									<>
										<i className="bi bi-send me-2"></i>
										Send ({selectedUsers.length})
									</>
								)}
							</Button>
						</div>
					</div>
				</ModalFooter>
			</Modal>

			{/* Dashboard Limit Modal */}
			<DashboardLimitModal
				isOpen={showLimitModal}
				toggle={handleLimitModalClose}
				savedDashboards={savedDashboards}
				onDelete={handleLimitModalDelete}
			/>

			{/* Success Modal */}
			<Modal isOpen={showSuccessModal} toggle={() => setShowSuccessModal(false)} size="sm">
				<ModalHeader toggle={() => setShowSuccessModal(false)}>
					<i className="bi bi-check-circle text-success me-2"></i>
					Success
				</ModalHeader>
				<ModalBody>
					<p className="mb-0">{successMessage}</p>
				</ModalBody>
				<ModalFooter>
					<Button color="primary" onClick={() => setShowSuccessModal(false)}>
						OK
					</Button>
				</ModalFooter>
			</Modal>

			{/* Error Modal */}
			<Modal isOpen={showErrorModal} toggle={() => setShowErrorModal(false)} size="sm">
				<ModalHeader toggle={() => setShowErrorModal(false)}>
					<i className="bi bi-exclamation-triangle text-danger me-2"></i>
					Error
				</ModalHeader>
				<ModalBody>
					<p className="mb-0">{errorMessage}</p>
				</ModalBody>
				<ModalFooter>
					<Button color="secondary" onClick={() => setShowErrorModal(false)}>
						Close
					</Button>
				</ModalFooter>
			</Modal>
		</>
	);
};

export default ShareDashboardModal;
